"use client";

import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import posthog from "posthog-js";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { submitExecuteWaitlist } from "./actions";
import { USE_CASES, waitlistInputSchema, type WaitlistInput } from "./schema";

export function WaitlistForm() {
  const [submitted, setSubmitted] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  const form = useForm<WaitlistInput>({
    resolver: zodResolver(waitlistInputSchema),
    defaultValues: {
      email: "",
      use_cases: [],
      free_form: "",
      consent_explicit: false,
    },
    mode: "onSubmit",
  });

  async function onSubmit(values: WaitlistInput) {
    setServerError(null);
    const result = await submitExecuteWaitlist(values);

    if (!result.ok) {
      if (result.error === "validation" && result.fieldErrors) {
        for (const [field, messages] of Object.entries(result.fieldErrors)) {
          form.setError(field as keyof WaitlistInput, {
            type: "server",
            message: messages?.[0] ?? "Invalid value",
          });
        }
      } else {
        setServerError("Something went wrong on our end. Try again in a minute?");
      }
      return;
    }

    // Best-effort: link this person's pre-signup session to their identified
    // record in PostHog so referrer / UTM history merges into the Person.
    // The authoritative event was already fired server-side; this is for analytics depth.
    try {
      posthog.identify(values.email);
    } catch {
      /* ignore — server event already landed */
    }

    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="border-nk-border bg-nk-card rounded-lg border p-8"
      >
        <h3 className="font-display text-nk-foreground text-2xl font-medium tracking-tight">
          You&rsquo;re on the list.
        </h3>
        <p className="text-nk-foreground-muted mt-3 text-base leading-relaxed">
          Thanks — we&rsquo;ll be in touch when Execute is ready. In the meantime, the simulator is
          live — give it a try.
        </p>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="border-nk-border bg-nk-card space-y-6 rounded-lg border p-6 md:p-8"
        noValidate
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="use_cases"
          render={() => (
            <FormItem>
              <FormLabel>
                Which features would make this an instant &ldquo;yes&rdquo; for you?
              </FormLabel>
              <div className="mt-2 grid gap-2.5">
                {USE_CASES.map(({ id, label }) => (
                  <FormField
                    key={id}
                    control={form.control}
                    name="use_cases"
                    render={({ field }) => {
                      const checked = field.value?.includes(id) ?? false;
                      return (
                        <FormItem className="flex items-start gap-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              id={`use-case-${id}`}
                              checked={checked}
                              onCheckedChange={(value) => {
                                const current = field.value ?? [];
                                if (value === true) {
                                  field.onChange([...current, id]);
                                } else {
                                  field.onChange(current.filter((v) => v !== id));
                                }
                              }}
                            />
                          </FormControl>
                          <Label
                            htmlFor={`use-case-${id}`}
                            className="text-nk-foreground cursor-pointer text-sm leading-relaxed font-normal"
                          >
                            {label}
                          </Label>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="free_form"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Anything else you&rsquo;d want this to do?{" "}
                <span className="text-nk-foreground-subtle font-normal">(optional)</span>
              </FormLabel>
              <FormControl>
                <Textarea
                  rows={4}
                  placeholder="Tell us what would actually make this useful to you."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="consent_explicit"
          render={({ field }) => (
            <FormItem className="flex items-start gap-3 space-y-0">
              <FormControl>
                <Checkbox
                  id="consent"
                  checked={field.value === true}
                  onCheckedChange={(value) => field.onChange(value === true)}
                />
              </FormControl>
              <div className="space-y-1.5">
                <Label
                  htmlFor="consent"
                  className="text-nk-foreground cursor-pointer text-sm leading-relaxed font-normal"
                >
                  I agree to be contacted about North Kove updates. We won&rsquo;t share your email
                  or use it for anything else.
                </Label>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        {serverError ? (
          <p role="alert" className="text-nk-danger text-sm">
            {serverError}
          </p>
        ) : null}

        <Button type="submit" className="w-full sm:w-auto" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Joining…" : "Join the waitlist"}
        </Button>
      </form>
    </Form>
  );
}
